import { useState, useEffect } from "react";
import { api } from "@/contexts/AuthContext";
import { Database, Server } from "lucide-react";

/**
 * Compact API / Redis health strip on the admin dashboard home.
 * Uses existing admin endpoints only (no new backend routes).
 */
export default function SystemHealthStrip() {
  const [health, setHealth] = useState({ api: "loading", redis: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = { api: "ok", redis: "unknown" };
      try {
        await api.get("/admin/dashboard");
      } catch {
        next.api = "down";
      }
      try {
        const r = await api.get("/admin/system/redis-stats");
        next.redis = r.data?.connected ? "ok" : "down";
      } catch {
        next.redis = "down";
      }
      if (!cancelled) setHealth(next);
    })();
    return () => { cancelled = true; };
  }, []);

  const pill = (label, state, Icon) => {
    const styles =
      state === "ok"
        ? "bg-[#DDF0E0] text-[#1F4D2C] border-[#B8E0C0]"
        : state === "loading"
          ? "bg-[#F3F2EE] text-[#5C6B60] border-[#E5E3DB]"
          : "bg-[#FFEFEF] text-[#7A1F1F] border-[#F5C2C2]";
    const text = state === "loading" ? "…" : state === "ok" ? "OK" : "Issue";
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${styles}`}
        data-testid={`health-${label.toLowerCase()}`}
      >
        <Icon className="w-3.5 h-3.5" aria-hidden />
        {label}: {text}
      </span>
    );
  };

  return (
    <div
      className="mb-6 flex flex-wrap items-center gap-2"
      data-testid="system-health-strip"
      role="status"
      aria-live="polite"
    >
      {pill("API", health.api, Server)}
      {pill("Redis", health.redis, Database)}
    </div>
  );
}
